class NewsletterPopup extends HTMLElement {
  constructor() {
    super();
    this.dialog = this.querySelector('dialog');
    this.closeButton = this.querySelector('[ref="closeButton"]');
    this.formWrapper = this.querySelector('[ref="formWrapper"]');
    this.successMessage = this.querySelector('[ref="successMessage"]');
    this.copyButton = this.querySelector('[ref="copyButton"]');
    this.discountCode = this.querySelector('[ref="discountCode"]');
    this.form = this.querySelector('form');

    this.delay = parseInt(this.getAttribute('delay') || '5', 10) * 1000;
    this.daysBetween = parseInt(this.getAttribute('days-between') || '7', 10);
    this.storageKey = 'newsletter-popup-dismissed';
    this.subscribedKey = 'newsletter-popup-subscribed';
  }

  connectedCallback() {
    if (this.hasSubscribed() || this.isRecentlyDismissed()) return;
    if (Shopify.designMode) return;

    this.timer = setTimeout(() => this.showDialog(), this.delay);

    this.closeButton?.addEventListener('click', () => this.closeDialog());
    this.dialog?.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.closeDialog();
    });
    this.dialog?.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.closeDialog();
    });
    this.form?.addEventListener('submit', (e) => this.handleSubmit(e));
    this.copyButton?.addEventListener('click', () => this.copyCode());

    // If the form was already submitted successfully (page reload after form post)
    if (this.form?.querySelector('[aria-describedby="NewsletterPopup-success"]')) {
      this.onSuccess();
      setTimeout(() => this.showDialog(), 300);
    }
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
  }

  showDialog() {
    if (!this.dialog) return;
    this.dialog.showModal();
    document.body.style.overflow = 'hidden';
  }

  closeDialog() {
    if (!this.dialog) return;
    this.dialog.classList.add('dialog-closing');
    this.setDismissed();

    const handleAnimationEnd = () => {
      this.dialog.classList.remove('dialog-closing');
      this.dialog.close();
      document.body.style.overflow = '';
      this.dialog.removeEventListener('animationend', handleAnimationEnd);
    };
    this.dialog.addEventListener('animationend', handleAnimationEnd);
  }

  handleSubmit(e) {
    e.preventDefault();
    const formData = new FormData(this.form);
    const email = formData.get('contact[email]');
    if (!email) return;

    const submitButton = this.form.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Sending...';
    }

    fetch(this.form.action, {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/json' },
      redirect: 'manual',
    })
      .then((response) => {
        // Shopify returns a redirect on success (status 0 for opaque redirect, or 2xx/3xx)
        if (response.ok || response.type === 'opaqueredirect' || response.status === 0) {
          this.onSuccess();
        } else {
          throw new Error('Submission failed');
        }
      })
      .catch(() => {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = 'Sign up';
        }
      });
  }

  onSuccess() {
    localStorage.setItem(this.subscribedKey, 'true');
    if (this.formWrapper) this.formWrapper.hidden = true;
    if (this.successMessage) this.successMessage.hidden = false;
  }

  copyCode() {
    const code = this.discountCode?.textContent?.trim();
    if (!code) return;

    navigator.clipboard.writeText(code).then(() => {
      if (this.copyButton) {
        this.copyButton.innerHTML =
          '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" width="20" height="20"><path d="m7.293 14.707-3-3a1 1 0 0 1 1.414-1.414L8 12.586l6.293-6.293a1 1 0 1 1 1.414 1.414l-7 7a1 1 0 0 1-1.414 0Z" fill="currentColor"/></svg>';
        setTimeout(() => {
          this.copyButton.innerHTML =
            '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" width="20" height="20"><path d="M7.5 3A1.5 1.5 0 0 0 6 4.5V5h8.5A1.5 1.5 0 0 1 16 6.5V15h.5A1.5 1.5 0 0 0 18 13.5v-9A1.5 1.5 0 0 0 16.5 3h-9Z" fill="currentColor"/><path d="M3 6.5A1.5 1.5 0 0 1 4.5 5h9A1.5 1.5 0 0 1 15 6.5v9a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 3 15.5v-9Z" fill="currentColor"/></svg>';
        }, 2000);
      }
    });
  }

  isRecentlyDismissed() {
    const dismissed = localStorage.getItem(this.storageKey);
    if (!dismissed) return false;
    const daysSince = (Date.now() - parseInt(dismissed, 10)) / (1000 * 60 * 60 * 24);
    return daysSince < this.daysBetween;
  }

  hasSubscribed() {
    return localStorage.getItem(this.subscribedKey) === 'true';
  }

  setDismissed() {
    localStorage.setItem(this.storageKey, Date.now().toString());
  }
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}
